import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { usePageMeta } from "../hooks/usePageMeta";

const CATS = [
  { value: "all", label: "All" },
  { value: "friendly", label: "Friendly" },
  { value: "challenge", label: "Challenge" },
  { value: "cup", label: "Cup" },
  { value: "prerelease", label: "Prerelease" },
];

const CAT_COLOR = { friendly: "#CD7F32", challenge: "#C0C0C0", cup: "#FFD700", prerelease: "#7000FF" };

export default function ResultsPage() {
  usePageMeta(
    "Past Tournament Results",
    "Standings and match results from past Pokémon tournaments, League Cups, Challenges and prereleases at Hex Labs Games."
  );
  const [category, setCategory] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/tournaments?category=${category}`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="mx-auto max-w-6xl px-5 lg:px-10 py-10 lg:py-16">
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
        PAST <span className="text-primary">RESULTS</span>
      </h1>
      <p className="mt-4 text-base md:text-lg text-neutral-400 max-w-xl">
        Every tournament imported from Tournament Operations Manager (.TDF). Prerelease events are archived but
        excluded from ranking points.
      </p>

      <Tabs value={category} onValueChange={setCategory} className="mt-8">
        <TabsList className="bg-white/5 border border-white/10 rounded-full flex-wrap h-auto">
          {CATS.map((c) => (
            <TabsTrigger
              key={c.value}
              value={c.value}
              data-testid={`results-tab-${c.value}`}
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4"
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading && <p className="mt-8 text-sm text-neutral-500">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="mt-8 text-sm text-neutral-500" data-testid="results-empty">
          No tournaments imported yet.
        </p>
      )}

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="results-grid">
        {items.map((t, i) => (
          <Link
            key={t.id}
            to={`/results/${t.id}`}
            data-testid={`tournament-card-${t.id}`}
            className="p-4 border border-white/10 rounded-md bg-white/[0.02] hover:border-primary/60 hover:scale-[1.02] transition-transform duration-300 animate-fade-up"
            style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
                style={{ color: CAT_COLOR[t.category], border: `1px solid ${CAT_COLOR[t.category]}55` }}
              >
                {t.category}
              </span>
              {!t.ranked && <span className="text-[10px] font-mono text-neutral-500">UNRANKED</span>}
            </div>
            <h3 className="mt-3 font-display text-base tracking-tight line-clamp-2">{t.name}</h3>
            <div className="mt-3 flex gap-4 text-[11px] font-mono text-neutral-500">
              <span>{format(new Date(t.date), "d MMM yyyy")}</span>
              <span>{t.player_count} players</span>
              <span>{t.round_count} rounds</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
